import React from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { AppRoutes } from "pages/constants/routes";
import { AppDispatch, RootState } from "store";
import { logout, UserState } from "store/userSlice";
import { PageSideBarState, setCollapsed } from "store/pageSideBarSlice";

import styles from "./PageSideBar.module.scss";
import { ReactComponent as Logo } from "assets/site/Logo_White.svg";
import { ReactComponent as Favicon } from "assets/site/Favicon_White.svg";
import {
  House,
  MessageCircleQuestion,
  Settings,
  Coins,
  HandCoins,
  LogOut,
  BookText,
  MonitorCog,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

interface TabProp {
  icon: React.ReactNode;
  label: string;
  routeTo: string;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
  minimal?: boolean;
}

const Tab: React.FC<TabProp> = ({
  icon,
  label,
  routeTo,
  target,
  rel,
  minimal = false,
}: TabProp) => (
  <Link to={routeTo} className={styles.option} target={target} rel={rel}>
    {icon}
    {!minimal && <span>{label}</span>}
  </Link>
);

const PageSideBar = () => {
  const userState: UserState = useSelector((state: RootState) => state.user);
  const { creditBalance } = useSelector((state: RootState) => state.payment);
  const { collapsed }: PageSideBarState = useSelector(
    (state: RootState) => state.pageSideBar,
  );
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const onLogOut = () => {
    dispatch(logout());
    navigate(AppRoutes.landing);
  };

  const docsUrl = process.env.REACT_APP_DOCS_URL || "http://localhost:3001";

  return (
    <div className={classNames(styles.sideBar, collapsed && styles.collapsed)}>
      <div className={styles.header}>
        {collapsed ? <Favicon className={styles.favicon} /> : <Logo />}
      </div>
      <div
        className={classNames(styles.content, collapsed && styles.collapsed)}
      >
        <div className={styles.topContainer}>
          <Tab
            icon={<House />}
            label="Home"
            routeTo={AppRoutes.home}
            minimal={collapsed}
          />
          <Tab
            icon={<HandCoins />}
            label="Get Credits"
            routeTo={AppRoutes.credit}
            minimal={collapsed}
          />
        </div>
        <div className={styles.bottomContainer}>
          {userState.hasPrivilegedAccess && (
            <Tab
              icon={<MonitorCog />}
              label="Admin Panel"
              routeTo={AppRoutes.admin}
              minimal={collapsed}
            />
          )}
          <Tab
            icon={<Settings />}
            label="Settings"
            routeTo={AppRoutes.settings}
            minimal={collapsed}
          />
          <Tab
            icon={<BookText />}
            label="Docs"
            target="_blank"
            rel="noopener noreferrer"
            routeTo={docsUrl}
            minimal={collapsed}
          />
          <Tab
            icon={<MessageCircleQuestion />}
            label="Support"
            routeTo={AppRoutes.support}
            minimal={collapsed}
          />
        </div>
      </div>
      <div className={classNames(styles.footer, collapsed && styles.collapsed)}>
        {!collapsed && (
          <div className={styles.creditBox}>
            <Coins />
            <span>{creditBalance.toFixed(2)}</span>
          </div>
        )}
        <div className={styles.personBox}>
          {!collapsed && (
            <div>
              <div>{userState.username}</div>
              <div>{userState.email}</div>
            </div>
          )}
          <div className={styles.exit} onClick={onLogOut}>
            <LogOut />
          </div>
        </div>
      </div>

      <div
        className={classNames(
          styles.collapseButton,
          collapsed && styles.collapsed,
        )}
        onClick={() => dispatch(setCollapsed(!collapsed))}
      >
        {collapsed ? <ChevronRight /> : <ChevronLeft />}
      </div>
    </div>
  );
};

export default PageSideBar;
